import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { TextInput } from '../shared/components/inputs/TextInput';
import { AuthPageLayout } from '../shared/components/containers/AuthPageLayout';
import { Form } from 'react-bootstrap';
import { PasswordInput } from '../shared/components/inputs/PasswordInput';
import { PrimaryButton } from '../shared/components/buttons/PrimaryButton';
import { BasicAnchor } from '../shared/components/links/BasicAnchor';
import { BrandHeader } from '../shared/components/headers/BrandHeader';
import { useAuth } from '../context/AuthContext';
import { ApiError } from '../api/client';

interface OwnProps {
}

const StyledUsernameInput = styled(TextInput)`
  width: 100%;
  max-width: 350px;
  margin-bottom: 20px;
`;

const StyledPasswordInput = styled(PasswordInput)`
  width: 100%;
  max-width: 350px;
  margin-bottom: 20px;
`;

const PasswordLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InFormHeader = styled.div`
  font-weight: 600;
  font-size: 1.3em;
  margin-bottom: 5px;
`;

const HeaderExplanation = styled(Form.Text)`
  margin-bottom: 15px;
  display: flex;
`;

const ButtonRow = styled.div`
  margin-top: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RegisterRow = styled.div`
  margin-top: 30px;
`;

const RegisterAnchor = styled(BasicAnchor)`
  margin-left: 1ch;
`;

const ErrorText = styled(Form.Text)`
  color: ${(props) => props.theme.colors.error};
  display: block;
  margin-bottom: 15px;
`;

const LoginPage = (props: OwnProps) => {
  const {} = props;
  const { t } = useTranslation();
  const { login } = useAuth();
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onLoginClick = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('login.errors.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageLayout>
      <BrandHeader/>

      <InFormHeader>{t('login.heading')}</InFormHeader>
      <HeaderExplanation muted>{t('login.explanation')}</HeaderExplanation>

      <Form.Label htmlFor="login-email">{t('login.emailLabel')}</Form.Label>
      <StyledUsernameInput id="login-email" onChange={setUsername} value={username}/>

      <PasswordLabelRow>
        <Form.Label htmlFor="login-password">{t('login.passwordLabel')}</Form.Label>
        <Form.Text muted>
          <BasicAnchor url='/forgot-password'>
            {t('login.forgotPassword')}
          </BasicAnchor>
        </Form.Text>
      </PasswordLabelRow>
      <StyledPasswordInput id="login-password" onChange={setPassword} value={password}/>

      {error && <ErrorText>{error}</ErrorText>}

      <ButtonRow>
        <PrimaryButton onClick={onLoginClick} disabled={submitting}>
          {submitting ? t('login.submitting') : t('login.submit')}
        </PrimaryButton>
      </ButtonRow>

      <RegisterRow>
        <Form.Text muted>
          {t('login.noAccount')}
          <RegisterAnchor url='/register'>{t('login.registerHere')}</RegisterAnchor>
        </Form.Text>
      </RegisterRow>
    </AuthPageLayout>
  );
};

export {
  LoginPage,
};
